///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// RTK Query endpoints for saved views sync (/api/user/views).
/// These are driven by the sync orchestrator (savedViewsSync.ts) and the
/// live-push store listener rather than component hooks: the local slice
/// is the source of truth and works fully offline/signed-out.
///

import type { PutSavedViewRequest, SavedViewsDelta } from "@shared-types"
import { authenticatedApi } from "./API/authenticatedApi"
import type { SavedView } from "./savedViewsSlice"

/** Convert a client view to the PUT request body (camelCase → wire) */
export function viewToPutRequest(view: SavedView): PutSavedViewRequest {
	return {
		name: view.name,
		camera: view.camera,
		focus_target_id: view.focusTargetId ?? null,
		layers: view.layers ?? [],
		tags: view.tags,
		created_at: view.createdAt,
		updated_at: view.updatedAt,
	}
}

export const savedViewsApi = authenticatedApi.injectEndpoints({
	endpoints: (builder) => ({
		getSavedViewsDelta: builder.query<SavedViewsDelta, { since: string | null }>({
			query: ({ since }) => ({
				url: since
					? `/api/user/views?since=${encodeURIComponent(since)}`
					: "/api/user/views",
			}),
			providesTags: ["UserCollections"],
		}),
		putSavedView: builder.mutation<{ success: boolean }, { id: string; body: PutSavedViewRequest }>({
			query: ({ id, body }) => ({
				url: `/api/user/views/${id}`,
				method: "PUT",
				body,
			}),
		}),
		deleteSavedView: builder.mutation<{ success: boolean }, string>({
			query: (id) => ({
				url: `/api/user/views/${id}`,
				method: "DELETE",
			}),
		}),
	}),
})

export const {
	useGetSavedViewsDeltaQuery,
	usePutSavedViewMutation,
	useDeleteSavedViewMutation,
} = savedViewsApi
